/**
 * Signature stroke-data math (Phase 12, PAD-03 / PAD-04).
 *
 * Canvas layout cannot be tested in happy-dom (RESEARCH Pitfall 9), so every
 * decision the signature pad makes about its strokes comes from these pure
 * functions. They work on the pad library's point-group shape structurally,
 * so libs/core never imports the library. Points are CSS px relative to the
 * canvas (RESEARCH Pattern 1).
 *
 * - `assessInk` is the ink sufficiency gate (PAD-03): it blocks empty, tap,
 *   and trivially small marks. Recompute it from `toData()` at Submit, never
 *   from a cached flag (Pitfall 3).
 * - `clampPixelRatio` and `fitPointGroups` let strokes survive DPR changes,
 *   rotation, and resize without clipping (PAD-04): scale down only, never
 *   upscale (Pitfall 6).
 *
 * Nothing here mutates its input: `toData()` returns the library's internal
 * array by reference.
 */
import type { SignatureInkState } from './signing';

/**
 * A stroke point. Structural (no index signature) so the pad library's
 * `BasicPoint` interface (`x`, `y`, `pressure`, `time`) is assignable.
 */
export interface PointLike {
  x: number;
  y: number;
}

/**
 * A stroke (one pointer-down to pointer-up). Structural so the pad library's
 * `PointGroup` interface (pen options + `points`) is assignable.
 */
export interface PointGroupLike {
  points: ReadonlyArray<PointLike>;
}

/** Result of {@link assessInk}. `ok` mirrors `state === 'ready'`. */
export interface InkAssessment {
  ok: boolean;
  state: SignatureInkState;
  /** Total points across every group. */
  points: number;
  /** Bounding-box width across every point of every group, CSS px. */
  bboxWidth: number;
  /** Sum of per-group path lengths, CSS px (groups are not joined). */
  pathLength: number;
}

/**
 * Ink sufficiency thresholds. Starting values are Claude's discretion and are
 * retuned in Phase 14 device QA. Never lower them to make a test pass
 * (RESEARCH Pitfall 10).
 */
export const INK_THRESHOLDS = Object.freeze({
  /** Total points across all strokes. */
  minPoints: 10,
  /** Bounding-box width floor, CSS px. */
  minBboxWidthPx: 40,
  /** Bounding-box width as a share of the pad width. */
  minBboxWidthRatio: 0.15,
  /** Total stroke path length, CSS px. */
  minPathLengthPx: 80,
} as const);

function countPoints(groups: ReadonlyArray<PointGroupLike>): number {
  return groups.reduce((sum, g) => sum + g.points.length, 0);
}

function bboxWidthOf(groups: ReadonlyArray<PointGroupLike>): number {
  let min = Infinity;
  let max = -Infinity;
  for (const g of groups) {
    for (const p of g.points) {
      min = Math.min(min, p.x);
      max = Math.max(max, p.x);
    }
  }
  return max >= min ? max - min : 0;
}

function pathLengthOf(groups: ReadonlyArray<PointGroupLike>): number {
  let total = 0;
  for (const g of groups) {
    for (let i = 1; i < g.points.length; i++) {
      const a = g.points[i - 1];
      const b = g.points[i];
      total += Math.hypot(b.x - a.x, b.y - a.y);
    }
  }
  return total;
}

/**
 * PAD-03 ink sufficiency gate. `box` is the pad's CSS box.
 *
 * - `empty`: zero points in total.
 * - `ready`: total points >= 10, bbox width >= max(40, 15% of pad width), and
 *   total path length >= 80 (all inclusive).
 * - `insufficient`: anything else (a single tap is never `ready`).
 */
export function assessInk(
  groups: ReadonlyArray<PointGroupLike>,
  box: { width: number; height: number },
): InkAssessment {
  const points = countPoints(groups);
  const bboxWidth = bboxWidthOf(groups);
  const pathLength = pathLengthOf(groups);
  if (points === 0) {
    return { ok: false, state: 'empty', points, bboxWidth, pathLength };
  }
  const minBbox = Math.max(
    INK_THRESHOLDS.minBboxWidthPx,
    INK_THRESHOLDS.minBboxWidthRatio * box.width,
  );
  const ready =
    points >= INK_THRESHOLDS.minPoints &&
    bboxWidth >= minBbox &&
    pathLength >= INK_THRESHOLDS.minPathLengthPx;
  const state: SignatureInkState = ready ? 'ready' : 'insufficient';
  return { ok: ready, state, points, bboxWidth, pathLength };
}

/** Upper bound for the canvas backing-store ratio (D-04). */
const MAX_PIXEL_RATIO = 2;

/**
 * D-04 DPR clamp: `min(max(dpr, 1), 2)`. NaN, zero, negative, and infinite
 * input returns 1 so a bad `devicePixelRatio` never sizes a 0px or huge
 * backing store.
 */
export function clampPixelRatio(dpr: number): number {
  if (!Number.isFinite(dpr) || dpr <= 0) return 1;
  return Math.min(Math.max(dpr, 1), MAX_PIXEL_RATIO);
}

/** Shrink factor from one box to another, or 1 when no shrink applies. */
function shrinkFactor(
  from: { w: number; h: number },
  to: { w: number; h: number },
): number {
  if (from.w <= 0 || from.h <= 0) return 1;
  const s = Math.min(to.w / from.w, to.h / from.h);
  // Never upscale; an unmeasured (0) or non-finite target keeps the strokes.
  return Number.isFinite(s) && s > 0 && s < 1 ? s : 1;
}

/**
 * PAD-04 resize replay: returns NEW groups and points, scaled uniformly by
 * `min(to.w / from.w, to.h / from.h)` only when the box shrank. Never
 * upscales. Every other point and group field (time, pressure, pen options)
 * is preserved. The input is never mutated (`toData()` hands out the
 * library's internal array).
 */
export function fitPointGroups<T extends PointGroupLike>(
  groups: ReadonlyArray<T>,
  from: { w: number; h: number },
  to: { w: number; h: number },
): T[] {
  const s = shrinkFactor(from, to);
  return groups.map((g) => ({
    ...g,
    points: g.points.map((p) => ({ ...p, x: p.x * s, y: p.y * s })),
  }));
}
